import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Loader2 } from "lucide-react";
import { http, usd, formatErr } from "@/lib/api";
import { Logo } from "@/components/brand/Logo";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Stars } from "@/components/site/Reviews";
import { RecipesAdmin, AccountAdmin } from "@/components/site/AdminExtras";

const ShipDialog = ({ order, onClose, onShipped }) => {
  const [carrier, setCarrier] = useState("USPS");
  const [tracking, setTracking] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await http.put(`/admin/orders/${order.session_id}/ship`, { carrier, tracking_number: tracking }, auth());
      toast.success(data?.email_sent ? "Marked shipped — customer emailed" : "Marked shipped");
      onShipped();
      onClose();
    } catch (e) {
      toast.error(formatErr(e));
    }
    setLoading(false);
  };
  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ship-dialog" className="bg-[#FDFBF7]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light italic">Mark as shipped</DialogTitle>
          <DialogDescription>{order?.order_ref} · {order?.customer_name || order?.customer_email}. The customer gets an email with tracking.</DialogDescription>
        </DialogHeader>
        <Select value={carrier} onValueChange={setCarrier}>
          <SelectTrigger data-testid="ship-carrier-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["USPS", "UPS", "FedEx"].map((c) => <SelectItem key={c} value={c} data-testid={`ship-carrier-${c}`}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="ship-tracking-input" placeholder="Tracking number" value={tracking} onChange={(e) => setTracking(e.target.value)} className="h-11 rounded-xl" />
        <button data-testid="ship-submit-button" disabled={loading || tracking.trim().length < 4} onClick={submit} className="flex h-11 items-center justify-center gap-2 rounded-full bg-[#C85A32] text-sm font-semibold text-[#FDFBF7] hover:bg-[#A84422] disabled:opacity-50">
          {loading && <Loader2 size={16} className="animate-spin" />} Save & email customer
        </button>
      </DialogContent>
    </Dialog>
  );
};

const ReviewsAdmin = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/reviews", auth());
      setItems(Array.isArray(data?.reviews) ? data.reviews : []);
    } catch (e) {
      toast.error(formatErr(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => {
    try {
      await http.put(`/admin/reviews/${id}`, { status }, auth());
      toast.success(`Review ${status}`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  if (items.length === 0) return <p data-testid="admin-reviews-empty" className="rounded-[22px] border border-[#E3D9CD] bg-white p-10 text-center text-sm text-[#6E6661]">No reviews yet. Buyers can review from their order page, and get an automatic request 5 days after you mark them shipped.</p>;
  return (
    <div data-testid="admin-reviews-list" className="grid gap-4 md:grid-cols-2">
      {items.map((r) => (
        <div key={r.id} data-testid={`admin-review-${r.id}`} className="flex gap-4 rounded-[22px] border border-[#E3D9CD] bg-white p-5">
          {r.photo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${r.photo_url}`} alt="" className="h-24 w-24 rounded-xl object-cover" />}
          <div className="flex-1">
            <div className="flex items-center justify-between"><Stars value={r.rating} size={14} /><span className="text-xs text-[#6E6661]">{r.status}</span></div>
            <p className="font-display mt-2 text-lg">{r.title}</p>
            <p className="mt-1 text-sm text-[#4A4543]">{r.body}</p>
            <p className="mt-2 text-xs text-[#6E6661]">{r.name}</p>
            <div className="mt-3 flex gap-2">
              {r.status !== "approved" && <button data-testid={`approve-review-${r.id}`} onClick={() => setStatus(r.id, "approved")} className="rounded-full bg-[#5E7153] px-4 py-1.5 text-xs font-semibold text-white">Approve</button>}
              {r.status !== "rejected" && <button data-testid={`reject-review-${r.id}`} onClick={() => setStatus(r.id, "rejected")} className="rounded-full border border-[#E3D9CD] px-4 py-1.5 text-xs">{r.status === "approved" ? "Unpublish" : "Reject"}</button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("ghp_admin_token") || ""}` } });

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      localStorage.setItem("ghp_admin_token", data.token);
      onDone();
    } catch (e2) {
      setErr(formatErr(e2));
    }
    setLoading(false);
  };
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="mx-auto mt-24 max-w-sm rounded-[28px] border border-[#E3D9CD] bg-white p-8">
      <h1 className="font-display text-3xl font-light italic">Store admin</h1>
      <p className="mt-2 text-sm text-[#6E6661]">Sign in to manage orders and inventory.</p>
      <Input data-testid="admin-login-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-6 h-12 rounded-xl" required />
      <Input data-testid="admin-login-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-3 h-12 rounded-xl" required />
      {err && <p data-testid="admin-login-error" className="mt-3 text-sm text-red-700">{err}</p>}
      <button data-testid="admin-login-button" disabled={loading} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#181615] py-3.5 font-semibold text-[#FDFBF7] hover:bg-[#C85A32]">
        {loading && <Loader2 size={16} className="animate-spin" />} Sign in
      </button>
    </form>
  );
};

const Stat = ({ label, value, testId }) => (
  <div className="rounded-[22px] border border-[#E3D9CD] bg-white p-6">
    <p className="eyebrow text-[0.62rem] text-[#6E6661]">{label}</p>
    <p data-testid={testId} className="font-display mt-3 text-4xl font-light">{value}</p>
  </div>
);

const Dashboard = ({ onLogout }) => {
  const [data, setData] = useState(null);
  const [stockInput, setStockInput] = useState("");
  const [shipping, setShipping] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data: d } = await http.get("/admin/orders", auth());
      setData(d);
      setStockInput(String(d?.stats?.stock ?? ""));
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error(formatErr(e));
    }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await http.put("/admin/inventory", { stock: Number(stockInput) }, auth());
      toast.success("Inventory updated");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const s = data?.stats ?? {};
  const orders = Array.isArray(data?.orders) ? data.orders : [];
  return (
    <div data-testid="admin-dashboard" className="mt-12">
      <div data-testid="admin-stats" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="In stock" value={s.stock ?? "–"} testId="admin-stat-stock" />
        <Stat label="Units sold" value={s.units_sold ?? 0} testId="admin-stat-sold" />
        <Stat label="Paid orders" value={s.paid_orders ?? 0} testId="admin-stat-orders" />
        <Stat label="Revenue" value={usd(s.revenue)} testId="admin-stat-revenue" />
      </div>
      <div className="mt-6 flex flex-wrap items-end gap-3 rounded-[22px] border border-[#E3D9CD] bg-white p-6">
        <div>
          <p className="eyebrow mb-2 text-[0.62rem] text-[#6E6661]">Set available inventory</p>
          <Input data-testid="admin-inventory-input" type="number" min={0} value={stockInput} onChange={(e) => setStockInput(e.target.value)} className="h-11 w-36 rounded-xl" />
        </div>
        <button data-testid="admin-update-inventory-button" onClick={save} className="h-11 rounded-full bg-[#C85A32] px-6 text-sm font-semibold text-[#FDFBF7] hover:bg-[#A84422]">Update</button>
      </div>
      <div className="mt-6 overflow-x-auto rounded-[22px] border border-[#E3D9CD] bg-white">
        <Table data-testid="admin-orders-table">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Ship to</TableHead><TableHead>Qty</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead>Fulfillment</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-[#6E6661]">No orders yet.</TableCell></TableRow>}
            {orders.map((o) => {
              const a = o.shipping_address || {};
              return (
                <TableRow key={o.session_id} data-testid={`admin-order-row-${o.session_id.slice(-8)}`}>
                  <TableCell className="whitespace-nowrap text-xs">{new Date(o.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-sm">{o.customer_name || "—"}<br /><span className="text-xs text-[#6E6661]">{o.customer_email || ""}</span></TableCell>
                  <TableCell className="text-xs">{a.line1 ? `${a.line1}${a.line2 ? ", " + a.line2 : ""}, ${a.city}, ${a.state} ${a.postal_code}` : "—"}</TableCell>
                  <TableCell>{o.quantity}</TableCell>
                  <TableCell>{usd(o.total)}</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2.5 py-1 text-xs ${o.payment_status === "paid" ? "bg-[#5E7153]/15 text-[#3F5236]" : "bg-[#F2EAE1] text-[#6E6661]"}`}>{o.payment_status}</span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {o.fulfillment_status === "shipped" ? (
                      <span data-testid={`admin-order-shipped-${o.session_id.slice(-8)}`}>{o.carrier} · <span className="font-mono-ghp">{o.tracking_number}</span></span>
                    ) : o.payment_status === "paid" ? (
                      <button data-testid={`admin-ship-button-${o.session_id.slice(-8)}`} onClick={() => setShipping(o)} className="rounded-full bg-[#181615] px-3.5 py-1.5 text-xs font-semibold text-[#FDFBF7] hover:bg-[#C85A32]">Mark shipped</button>
                    ) : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <ShipDialog order={shipping} onClose={() => setShipping(null)} onShipped={load} />
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("ghp_admin_token"));
  const logout = useCallback(() => {
    localStorage.removeItem("ghp_admin_token");
    setAuthed(false);
  }, []);
  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#FDFBF7]">
      <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
        <div className="flex items-center justify-between">
          <Logo />
          {authed && <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-sm text-[#6E6661] hover:text-[#181615]"><LogOut size={16} /> Sign out</button>}
        </div>
        {authed ? (
          <Tabs defaultValue="orders" className="mt-10">
            <TabsList className="h-auto flex-wrap rounded-full bg-[#F2EAE1]">
              <TabsTrigger data-testid="admin-tab-orders" value="orders" className="rounded-full">Orders & inventory</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-reviews" value="reviews" className="rounded-full">Reviews</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-recipes" value="recipes" className="rounded-full">Recipes</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-account" value="account" className="rounded-full">Account</TabsTrigger>
            </TabsList>
            <TabsContent value="orders"><Dashboard onLogout={logout} /></TabsContent>
            <TabsContent value="reviews" className="mt-8"><ReviewsAdmin /></TabsContent>
            <TabsContent value="recipes" className="mt-8"><RecipesAdmin /></TabsContent>
            <TabsContent value="account" className="mt-8"><AccountAdmin /></TabsContent>
          </Tabs>
        ) : <Login onDone={() => setAuthed(true)} />}
      </div>
    </main>
  );
}
